import { useEffect, useRef, useState } from 'react';

const PIN_LENGTH = 6;
const ERROR_DISPLAY_MS = 4000;
const DIGIT_KEY_PATTERN = /^[0-9]$/;

export interface UsePinInputResult {
  pin: string;
  pinLength: number;
  errorVisible: boolean;
  appendDigit: (digit: string) => void;
  handleBackspace: () => void;
  handleConfirm: () => void;
}

/**
 * Estado e efeitos do teclado numérico virtual da tela de PIN (HUB-235,
 * CA-08/CA-09): contador/bullets, limpeza + fade-out de erro em 4s,
 * auto-confirmação no 6º dígito e suporte a teclado físico. Extraído de
 * `PinEntryScreen` para manter o componente de apresentação enxuto.
 */
export function usePinInput(
  onSubmit: (pin: string) => void,
  isLoading: boolean,
  errorMessage: string | null
): UsePinInputResult {
  const [pin, setPin] = useState('');
  const [errorVisible, setErrorVisible] = useState(false);
  const wasLoadingRef = useRef(isLoading);
  const submittedRef = useRef(false);

  useEffect(() => {
    const wasLoading = wasLoadingRef.current;
    wasLoadingRef.current = isLoading;
    if (!(wasLoading && !isLoading && errorMessage)) return undefined;

    setPin('');
    setErrorVisible(true);
    const timer = setTimeout(() => setErrorVisible(false), ERROR_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [isLoading, errorMessage]);

  // Auto-confirmação no 6º dígito (CA-08) como efeito, nunca dentro do
  // updater de `setPin`: o React (StrictMode) invoca updaters em duplicata
  // para checar pureza — um `onSubmit` ali dispararia a autenticação 2x
  // (mesma classe de bug do double-POST da HUB-119).
  useEffect(() => {
    if (pin.length !== PIN_LENGTH) {
      submittedRef.current = false;
      return;
    }
    if (submittedRef.current) return;
    submittedRef.current = true;
    onSubmit(pin);
  }, [pin, onSubmit]);

  function appendDigit(digit: string): void {
    if (isLoading) return;
    setPin((current) => (current.length >= PIN_LENGTH ? current : current + digit));
  }

  function handleBackspace(): void {
    if (isLoading) return;
    setPin((current) => current.slice(0, -1));
  }

  function handleConfirm(): void {
    if (isLoading || pin.length !== PIN_LENGTH || submittedRef.current) return;
    submittedRef.current = true;
    onSubmit(pin);
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (isLoading) return;
      if (DIGIT_KEY_PATTERN.test(event.key)) {
        appendDigit(event.key);
      } else if (event.key === 'Backspace') {
        handleBackspace();
      } else if (event.key === 'Enter') {
        handleConfirm();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLoading, pin]);

  return { pin, pinLength: PIN_LENGTH, errorVisible, appendDigit, handleBackspace, handleConfirm };
}
